import type { AnchorHTMLAttributes } from 'react';
import NextLink from 'next/link';
import { cx } from '../utils/cx';

export interface LinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  href?: string;
  hrefTemplate?: string;
  target?: AnchorHTMLAttributes<HTMLAnchorElement>['target'];
  rel?: AnchorHTMLAttributes<HTMLAnchorElement>['rel'];
  prefetch?: boolean;
  unstyled?: boolean;
  external?: boolean;
}

const warnedMissingHref = new Set<string>();
const URL_SCHEME_PATTERN = /^[a-zA-Z][a-zA-Z\d+\-.]*:/;
const LOCALE_PREFIXED_HREF = /^\/([a-z]{2}(?:-[A-Za-z]{2})?)(\/|$)/i;

function warnOnce(key: string, message: string, data?: unknown) {
  if (process.env.NODE_ENV === 'production') return;
  if (warnedMissingHref.has(key)) return;
  warnedMissingHref.add(key);
  // eslint-disable-next-line no-console
  console.warn(message, data);
}

function hasUrlScheme(value: string): boolean {
  return value.startsWith('//') || URL_SCHEME_PATTERN.test(value);
}

/**
 * Peel stacked locale prefixes and a stray leading slash until a bare
 * absolute URL remains. Returns null when the href is an ordinary path.
 *
 * `/en/https://doi.org/...` and `/en/en/https://doi.org/...` must classify
 * as external on both SSR and the client — otherwise one render emits
 * `<a href="https://..." target rel>` and the other emits NextLink
 * `href="/en/https://..."`, which fails hydration.
 */
function unwrapAbsoluteHref(href: string): string | null {
  const trimmed = href.trim();
  if (!trimmed) return null;

  let current = trimmed;
  for (let depth = 0; depth < 8; depth += 1) {
    if (hasUrlScheme(current)) return current;
    if (current.startsWith('/') && hasUrlScheme(current.slice(1))) {
      return current.slice(1);
    }

    const match = LOCALE_PREFIXED_HREF.exec(current);
    if (!match) break;
    current = current.slice(match[1].length + 1);
  }

  if (hasUrlScheme(current)) return current;
  if (current.startsWith('/') && hasUrlScheme(current.slice(1))) {
    return current.slice(1);
  }
  return null;
}

function isAbsoluteHref(href: string): boolean {
  // A URL scheme anywhere in the href means the target is absolute — even
  // when it arrives stacked behind one or more locale prefixes
  // (/en/https://..., /en/en/https://...). Classifying it HERE, before
  // NextLink can see it, guarantees both renders emit the same external
  // <a> (bare href + target + rel). Without this guard one side could reach
  // NextLink's internal branch with a /en/https://... href while the other
  // emitted the bare external anchor, failing hydration on the
 // learn-article Sources & evidence DOI chips.
  return href.includes('://') || unwrapAbsoluteHref(href) !== null;
}

function unwrapLocalizedAbsoluteHref(href: string): string {
  return unwrapAbsoluteHref(href) ?? href.trim();
}

export function Link({
  href,
  hrefTemplate,
  target,
  rel,
  className,
  prefetch,
  unstyled = false,
  external = false,
  ...rest
}: LinkProps) {
  const resolvedHref = unwrapLocalizedAbsoluteHref(String(href ?? hrefTemplate ?? '').trim());

  if (!resolvedHref) {
    warnOnce(`missing-href::${String(hrefTemplate ?? '')}`, '[Link] Missing `href`. Rendering a non-interactive element instead.', {
      href,
      hrefTemplate,
    });

    return (
      <span className={cx(unstyled ? 'text-tertiary' : 'underline-offset-4 text-tertiary', className)} aria-disabled="true">
        {rest.children}
      </span>
    );
  }

  const isHash = resolvedHref.startsWith('#');
  const isExternal = external || isAbsoluteHref(resolvedHref);
  const isInternal = !isExternal && !isHash;

  const safeRel = target === '_blank' ? rel ?? 'noopener noreferrer' : rel;
  const commonClassName = unstyled ? cx(className) : cx('underline-offset-4 hover:underline', className);

  if (!isInternal) {
    return <a {...rest} href={resolvedHref} target={target} rel={safeRel} className={commonClassName} />;
  }

  return <NextLink href={resolvedHref} prefetch={prefetch} className={commonClassName} {...(rest as any)} />;
}

Link.displayName = 'Link';